import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Building2,
  CheckCircle2,
  FileText,
  FolderKanban,
  Settings,
  Shield,
  TrendingDown,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import { getOrgMembers, getOrgStats } from '../api/api';
import { useAuth } from '../context/AuthContext';

const PINK = '#c44569';
const CARD_STYLE = { borderColor: '#f4d6df', background: '#fff' };

function StatCard({ label, value, Icon, color, loading }) {
  return (
    <div className="rounded-2xl border p-5 shadow-sm" style={CARD_STYLE}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
            {loading ? '…' : (value ?? 0)}
          </p>
        </div>
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ background: `${color}18`, color }}
        >
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, label, Icon, description, color }) {
  return (
    <Link
      to={to}
      className="rounded-2xl border p-4 shadow-sm flex items-start gap-3 hover:-translate-y-0.5 hover:shadow-md transition-all"
      style={CARD_STYLE}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl mt-0.5"
        style={{ background: `${color}18`, color }}
      >
        <Icon size={17} />
      </div>
      <div className="min-w-0">
        <p className="font-bold text-sm text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
    </Link>
  );
}

function MemberRow({ member }) {
  const roleBadgeColor = {
    organization_head: { bg: '#fce0e8', color: PINK },
    admin: { bg: '#dbeafe', color: '#1d4ed8' },
    finance_manager: { bg: '#dcfce7', color: '#15803d' },
    accountant: { bg: '#fef9c3', color: '#a16207' },
    project_manager: { bg: '#f3e8ff', color: '#7e22ce' },
    employee: { bg: '#f1f5f9', color: '#475569' },
    viewer: { bg: '#fafafa', color: '#64748b' },
  }[member.org_role] || { bg: '#f1f5f9', color: '#475569' };

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3" style={{ background: '#f8fafc' }}>
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm shrink-0"
          style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)', color: '#fff' }}
        >
          {(member.name || 'U').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-slate-800 truncate">{member.name || 'Unknown'}</p>
          <p className="text-xs text-slate-500 truncate">{member.email || ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={roleBadgeColor}
        >
          {(member.org_role || 'employee').replace(/_/g, ' ')}
        </span>
        {Number(member.is_active) ? (
          <CheckCircle2 size={14} className="text-emerald-500" />
        ) : (
          <XCircle size={14} className="text-rose-400" />
        )}
      </div>
    </div>
  );
}

export default function OrgAdminDashboardPage() {
  const { activeOrg, canViewDashboardCards, canViewAnalytics, canViewUsers, canManageMembers, isOrgHead, isOrgAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);

  useEffect(() => {
    if (!activeOrg?.id) return;

    setStatsLoading(true);
    getOrgStats(activeOrg.id)
      .then((res) => {
        setStats(res?.data?.data || null);
      })
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));

    if (canViewUsers) {
      setMembersLoading(true);
      getOrgMembers(activeOrg.id, { page: 1, limit: 8 })
        .then((res) => setMembers(res?.data?.data?.members || []))
        .catch(() => setMembers([]))
        .finally(() => setMembersLoading(false));
    } else {
      setMembersLoading(false);
    }
  }, [activeOrg?.id, canViewUsers]);

  const roleTitle = isOrgHead ? 'Organization Head' : isOrgAdmin ? 'Organization Admin' : 'Org Manager';

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-10">

      {/* Header */}
      <section className="rounded-3xl border p-6 shadow-sm" style={CARD_STYLE}>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: PINK }}>
          {roleTitle} Workspace
        </p>
        <h1 className="text-2xl font-black text-slate-900 mt-1">
          {activeOrg?.name || 'Organization Dashboard'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage your organization, control permissions, oversee team members and track financial activity.
        </p>
      </section>

      {/* KPI Stats */}
      {canViewDashboardCards ? (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Members"      value={stats?.total_members}      Icon={Users}         color="#c44569" loading={statsLoading} />
          <StatCard label="Projects"     value={stats?.total_projects}     Icon={FolderKanban}  color="#60a5fa" loading={statsLoading} />
          <StatCard label="Transactions" value={stats?.total_transactions}  Icon={FileText}     color="#34d399" loading={statsLoading} />
          <StatCard label="Net Balance"  value={stats?.net_balance != null ? Number(stats.net_balance).toLocaleString() : '-'} Icon={BarChart3} color="#a78bfa" loading={statsLoading} />
        </section>
      ) : null}

      {canViewAnalytics ? (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border p-4 shadow-sm" style={CARD_STYLE}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-emerald-500" />
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Total Income</span>
            </div>
            <p className="text-xl font-extrabold text-emerald-700">
              {statsLoading ? '…' : (stats?.total_income != null ? Number(stats.total_income).toLocaleString() : '-')}
            </p>
          </div>
          <div className="rounded-2xl border p-4 shadow-sm" style={CARD_STYLE}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown size={16} className="text-rose-500" />
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Total Expenses</span>
            </div>
            <p className="text-xl font-extrabold text-rose-700">
              {statsLoading ? '…' : (stats?.total_expenses != null ? Number(stats.total_expenses).toLocaleString() : '-')}
            </p>
          </div>
        </section>
      ) : null}

      {/* Quick Actions */}
      <section>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">Management Modules</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickLink to="/org-admin/members"     label="Team Members"          Icon={Users}        description="View, manage and adjust roles for your team." color="#c44569" />
          <QuickLink to="/org-admin/permissions" label="Permission Management" Icon={Shield}       description="Control what each role and user can access." color="#7e22ce" />
          <QuickLink to="/org-admin/transactions" label="Transactions"          Icon={FileText}    description="Review and manage all financial transactions." color="#0ea5e9" />
          <QuickLink to="/org-admin/projects"    label="Projects"              Icon={FolderKanban} description="Manage active projects in your organization." color="#16a34a" />
          <QuickLink to="/org-admin/analytics"   label="Analytics & Reports"   Icon={BarChart3}   description="Review financial trends and performance data." color="#ea580c" />
          <QuickLink to="/org-admin/invitations" label="Invitations"           Icon={Bell}        description="Invite new members to join your organization." color="#0891b2" />
          <QuickLink to="/org-admin/history"     label="History"               Icon={BarChart3}   description="Inspect project-level transaction history." color="#ca8a04" />
          <QuickLink to="/org-admin/settings"    label="Org Settings"          Icon={Settings}    description="Update organization profile and preferences." color="#64748b" />
          <QuickLink to="/profile"               label="My Profile"            Icon={Building2}   description="Manage your personal account settings." color="#a855f7" />
        </div>
      </section>

      {/* Team Members Preview */}
      {canViewUsers ? (
        <section className="rounded-3xl border p-5 shadow-sm" style={CARD_STYLE}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-slate-900">Team Members</h2>
              <p className="text-xs text-slate-500 mt-0.5">Recent members in your organization</p>
            </div>
            <Link
              to="/org-admin/members"
              className="text-xs font-semibold rounded-xl px-3 py-1.5 transition hover:opacity-80"
              style={{ background: '#fce0e8', color: PINK }}
            >
              View All →
            </Link>
          </div>
          {membersLoading ? (
            <p className="text-sm text-slate-400 py-4 text-center">Loading members…</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No members found. Invite your team to get started.</p>
          ) : (
            <div className="space-y-2">
              {members.map((m) => (
                <MemberRow key={m.id} member={m} />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Permission Notice */}
      <section className="rounded-3xl border p-5 shadow-sm" style={{ ...CARD_STYLE, borderColor: '#e0e7ff', background: '#f8f9ff' }}>
        <div className="flex items-start gap-3">
          <Shield size={20} className="shrink-0 mt-0.5" style={{ color: '#7e22ce' }} />
          <div>
            <p className="font-bold text-slate-900 text-sm">Permission Control Active</p>
            <p className="text-xs text-slate-500 mt-1">
              As {roleTitle}, you can grant or deny access for any role or individual user in your organization.
              Go to <Link to="/org-admin/permissions" className="font-semibold" style={{ color: '#7e22ce' }}>Permission Management</Link> to control module access, screen visibility, and CRUD actions for every role.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
